import { SUPPORTED_LANGUAGES } from '@kiku/config/constants';
import { z } from 'zod';

// --- Tenant ---

export const tenantSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  createdAt: z.date(),
  updatedAt: z.date()
});

export type Tenant = z.infer<typeof tenantSchema>;

export const createTenantInputSchema = z.object({
  name: z.string().min(1).max(200)
});

export type CreateTenantInput = z.infer<typeof createTenantInputSchema>;

export const updateTenantInputSchema = z.object({
  name: z.string().min(1).max(200)
});

export type UpdateTenantInput = z.infer<typeof updateTenantInputSchema>;

// --- Organization ---

export const organizationSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  name: z.string(),
  slug: z.string(),
  workosOrgId: z.string().nullable(),
  language: z.enum(SUPPORTED_LANGUAGES),
  logoKey: z.string().nullable(),
  timezone: z.string(),
  createdAt: z.date(),
  updatedAt: z.date()
});

export type Organization = z.infer<typeof organizationSchema>;

const orgNameSlugFields = {
  name: z.string().min(1).max(200),
  slug: z
    .string()
    .min(1)
    .max(5)
    .regex(/^[a-z0-9-]+$/, 'Slug must contain only lowercase letters, numbers, and hyphens')
};

export const createOrgInputSchema = z.discriminatedUnion('tenantMode', [
  z.object({
    tenantMode: z.literal('new'),
    tenantName: z.string().min(1).max(200),
    ...orgNameSlugFields
  }),
  z.object({
    tenantMode: z.literal('existing'),
    tenantId: z.string().uuid(),
    ...orgNameSlugFields
  })
]);

export type CreateOrgInput = z.infer<typeof createOrgInputSchema>;

export const updateOrgInputSchema = z
  .object({
    name: z.string().min(1).max(200).optional(),
    slug: z
      .string()
      .min(1)
      .max(5)
      .regex(/^[a-z0-9-]+$/, 'Slug must contain only lowercase letters, numbers, and hyphens')
      .optional(),
    language: z.enum(SUPPORTED_LANGUAGES).optional()
  })
  .refine(
    (data) => data.name !== undefined || data.slug !== undefined || data.language !== undefined,
    { message: 'At least one of name, slug, or language must be provided' }
  );

export const updateOrgSettingsInputSchema = z.object({
  timezone: z.string().min(1).max(100)
});

export type UpdateOrgSettingsInput = z.infer<typeof updateOrgSettingsInputSchema>;

export type UpdateOrgInput = z.infer<typeof updateOrgInputSchema>;

// --- Department ---

export const departmentSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  orgId: z.string().uuid(),
  name: z.string(),
  slug: z.string(),
  createdAt: z.date(),
  updatedAt: z.date()
});

export type Department = z.infer<typeof departmentSchema>;

export const createDeptInputSchema = z.object({
  orgId: z.string().uuid(),
  name: z.string().min(1).max(200),
  slug: z
    .string()
    .min(1)
    .max(30)
    .regex(/^[a-z0-9-]+$/, 'Slug must contain only lowercase letters, numbers, and hyphens')
});

export type CreateDeptInput = z.infer<typeof createDeptInputSchema>;

export const updateDeptInputSchema = z
  .object({
    name: z.string().min(1).max(200).optional(),
    slug: z
      .string()
      .min(1)
      .max(30)
      .regex(/^[a-z0-9-]+$/, 'Slug must contain only lowercase letters, numbers, and hyphens')
      .optional()
  })
  .refine((data) => data.name !== undefined || data.slug !== undefined, {
    message: 'At least one of name or slug must be provided'
  });

export type UpdateDeptInput = z.infer<typeof updateDeptInputSchema>;

// --- Feedback (admin view) ---

export const adminFeedbackEntrySchema = z.object({
  id: z.string().uuid(),
  thumbs: z.enum(['up', 'down']),
  notes: z.string().nullable(),
  createdAt: z.date(),
  sessionId: z.string().uuid(),
  modality: z.string(),
  candidateName: z.string(),
  jobTitle: z.string(),
  orgName: z.string(),
  applicationId: z.string().uuid(),
  jobId: z.string().uuid(),
  orgSlug: z.string(),
  deptSlug: z.string()
});

export type AdminFeedbackEntry = z.infer<typeof adminFeedbackEntrySchema>;

export const listAdminFeedbackQuerySchema = z.object({
  page: z
    .union([z.number(), z.string().transform(Number)])
    .pipe(z.number().int().min(1))
    .default(1),
  limit: z
    .union([z.number(), z.string().transform(Number)])
    .pipe(z.number().int().min(1).max(100))
    .default(20),
  thumbs: z.enum(['up', 'down']).optional(),
  modality: z.enum(['text', 'voice']).optional(),
  hasNotes: z.union([z.boolean(), z.string().transform((v) => v === 'true')]).optional()
});

export type ListAdminFeedbackQuery = z.infer<typeof listAdminFeedbackQuerySchema>;

const feedbackStatsBucketSchema = z.object({
  total: z.number(),
  bad: z.number(),
  prevTotal: z.number(),
  prevBad: z.number()
});

export const adminFeedbackStatsSchema = z.object({
  chat: feedbackStatsBucketSchema,
  voice: feedbackStatsBucketSchema
});

export type AdminFeedbackStats = z.infer<typeof adminFeedbackStatsSchema>;

// --- Errors ---

export class AdminNotFoundError extends Error {
  constructor(resource: string) {
    super(`${resource} not found`);
    this.name = 'AdminNotFoundError';
  }
}

export class AdminConflictError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AdminConflictError';
  }
}
